import React, { useState, useEffect, useMemo, useId, useCallback } from 'react';
import Chart from 'react-apexcharts';
import { Skeleton } from '../ui/Skeleton';
import { computeTrailingWindowAverage } from '../../utils/timeSeriesSmoothing';
import { getDarkLineAreaChartBase, mergeChartOptions } from '../../utils/darkChartTheme';
import {
  buildCategoryZoomChartEvents,
  sliceByCategoryZoom,
  type CategoryZoomRange,
} from './chartZoomUtils';
import {
  CHART_TOOLBAR_ROW_CLASS,
  CHART_TOOLBAR_SELECT_CLASS,
  CHART_TOOLBAR_TOGGLE_GROUP_CLASS,
  chartToolbarToggleBtnClass,
} from './chartToolbarTheme';

const CHART_TYPES = [
  { id: 'bar', label: 'Bars' },
  { id: 'area', label: 'Area' },
  { id: 'stack', label: 'Stack' },
];

const PERIODS = [
  { id: 'weekly', label: 'Week' },
  { id: 'monthly', label: 'Month' },
  { id: 'yearly', label: 'Year' },
];

const MA_WINDOWS = [
  { id: 'weekly', label: 'Week', days: 7 },
  { id: 'monthly', label: 'Month', days: 30 },
  { id: 'yearly', label: 'Year', days: 365 },
];

/**
 * Reusable Deployment Frequency chart with period/chart-type toggles and average line.
 * @param {string[]} dates - Array of date strings (YYYY-MM-DD)
 * @param {number[]} counts - Array of counts per day (same length as dates)
 * @param {number} windowDays - Number of days in the window (for average: total/windowDays)
 * @param {string} period - 'weekly' | 'monthly' | 'yearly'
 * @param {string} chartType - 'area' | 'bar' | 'stack'
 * @param {(id: string) => void} onPeriodChange
 * @param {(id: string) => void} onChartTypeChange
 * @param {string} [title='Deployment Frequency']
 * @param {string} [seriesName='Merges per day']
 * @param {(v: number) => string} [tooltipYFormatter] - e.g. (v) => `${v} merge(s) that day`
 * @param {number} [height=220]
 */
const DeploymentFrequencyChart = ({
  dates = [],
  counts = [],
  windowDays: _windowDays,
  period,
  chartType,
  onPeriodChange,
  onChartTypeChange,
  title = 'Deployment Frequency',
  seriesName = 'Merges per day',
  tooltipYFormatter = (v) => `${v} merge${v !== 1 ? 's' : ''} that day`,
  height = 320,
  loading = false,
}: any) => {
  const chartUid = useId().replace(/:/g, '');
  const [zoomRange, setZoomRange] = useState<CategoryZoomRange>(null);
  const [useMovingAverage, setUseMovingAverage] = useState(true);
  const [maWindow, setMaWindow] = useState('weekly'); // weekly|monthly|yearly

  const n = dates.length;

  const maDays = (MA_WINDOWS.find((w) => w.id === maWindow) || MA_WINDOWS[0]).days;

  const seriesToRender = useMemo(() => {
    if (!useMovingAverage) return counts;
    return computeTrailingWindowAverage({ dates, values: counts, windowDays: maDays });
  }, [useMovingAverage, dates, counts, maDays]);

  const displayedValues = sliceByCategoryZoom<number>(seriesToRender, zoomRange);

  const displayedMean =
    displayedValues.length > 0
      ? displayedValues.reduce((a, b) => a + b, 0) / displayedValues.length
      : 0;

  const displayedRawTotal = sliceByCategoryZoom<number>(counts, zoomRange).reduce(
    (a, b) => a + b,
    0
  );

  // Reset zoom stats when the underlying series changes (e.g. period switch).
  useEffect(() => {
    setZoomRange(null);
  }, [dates, counts, period]);

  const showXLabel = useCallback(
    (dataPointIndex) => {
      if (n <= 14) return true;
      if (n <= 35) return dataPointIndex % 5 === 0 || dataPointIndex === n - 1;
      if (period === 'yearly') {
        return (
          dataPointIndex === 0 || dataPointIndex === n - 1 || dataPointIndex === Math.floor(n / 2)
        );
      }
      const step = Math.max(1, Math.floor(n / 12));
      return dataPointIndex % step === 0 || dataPointIndex === n - 1;
    },
    [n, period]
  );

  const categories = useMemo(
    () =>
      dates.map((d) => {
        const [, m, day] = d.split('-');
        return `${String(Number(day)).padStart(2, '0')}/${String(Number(m)).padStart(2, '0')}`;
      }),
    [dates]
  );

  const handleZoomRangeChange = useCallback((range: CategoryZoomRange) => {
    setZoomRange(range);
  }, []);

  const chartOptions = useMemo(
    () =>
      mergeChartOptions(
        getDarkLineAreaChartBase({
          enableToolbar: true,
          chartType: chartType === 'bar' ? 'bar' : 'area',
        }),
        {
          chart: {
            id: `deployment-frequency-${chartUid}`,
            animations: { enabled: true, easing: 'easeinout', speed: 400 },
            stacked: chartType === 'stack',
            ...buildCategoryZoomChartEvents(n, handleZoomRangeChange),
          },
          plotOptions: {
            bar: {
              borderRadius: 4,
              columnWidth: '70%',
              distributed: false,
            },
          },
          dataLabels: { enabled: false },
          xaxis: {
            categories,
            ...(chartType === 'bar' && { tickPlacement: 'on' as const }),
            ...(period === 'yearly' && { tickAmount: 2 }),
            labels: {
              maxRotation: 45,
              hideOverlappingLabels: true,
              formatter: (value, _labelValue, opts) => (showXLabel(opts?.i ?? 0) ? value : ''),
            },
          },
          yaxis: {
            labels: {
              formatter: (v) => `${Number(v).toFixed(1)} per day`,
            },
            tickAmount: 4,
            min: 0,
          },
          colors: ['#7C3AED'],
          grid: {
            ...(chartType === 'bar' && { padding: { left: 10, right: 10 } }),
            xaxis: { lines: { show: false } },
          },
          tooltip: {
            x: {
              formatter: (_val, { dataPointIndex }) => {
                const d = dates[dataPointIndex];
                if (!d) return _val;
                const [, m, day] = d.split('-');
                return `${String(Number(m)).padStart(2, '0')}/${String(Number(day)).padStart(2, '0')}/${d.slice(0, 4)}`;
              },
            },
            y: { formatter: tooltipYFormatter },
          },
        }
      ),
    [chartUid, chartType, categories, dates, handleZoomRangeChange, n, period, showXLabel, tooltipYFormatter]
  );

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div>
          <h4 className="text-base font-semibold text-slate-100">{title}</h4>
          <p className="text-2xl font-bold text-slate-100 mt-1">
            {loading ? (
              <span className="text-slate-400">Loading…</span>
            ) : (
              <>
                {useMovingAverage ? (
                  <>
                    Moving avg:{' '}
                    <span className="text-brand-purple">{displayedMean.toFixed(1)} per day</span>
                    <span className="text-slate-400 font-normal text-lg ml-2">
                      · {maDays}d window
                    </span>
                  </>
                ) : (
                  <>
                    Average:{' '}
                    <span className="text-brand-purple">{displayedMean.toFixed(1)} per day</span>
                    <span className="text-slate-400 font-normal text-lg ml-2">
                      · {displayedRawTotal.toLocaleString()} total
                    </span>
                  </>
                )}
              </>
            )}
          </p>
        </div>
        <div className={CHART_TOOLBAR_ROW_CLASS}>
          <div className={CHART_TOOLBAR_TOGGLE_GROUP_CLASS}>
            <button
              type="button"
              onClick={() => setUseMovingAverage(false)}
              disabled={loading}
              className={chartToolbarToggleBtnClass(!useMovingAverage)}
              aria-pressed={!useMovingAverage}
            >
              Raw
            </button>
            <button
              type="button"
              onClick={() => setUseMovingAverage(true)}
              disabled={loading}
              className={`${chartToolbarToggleBtnClass(useMovingAverage)} border-l border-dark-border`}
              aria-pressed={useMovingAverage}
            >
              MA
            </button>
          </div>
          <select
            aria-label="Moving average window"
            value={maWindow}
            onChange={(e) => setMaWindow(e.target.value)}
            disabled={loading || !useMovingAverage}
            className={CHART_TOOLBAR_SELECT_CLASS}
          >
            {MA_WINDOWS.map(({ id, label }) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
          <select
            aria-label="Chart type"
            value={chartType}
            onChange={(e) => onChartTypeChange(e.target.value)}
            disabled={loading}
            className={CHART_TOOLBAR_SELECT_CLASS}
          >
            {CHART_TYPES.map(({ id, label }) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
          <select
            aria-label="Period"
            value={period}
            onChange={(e) => onPeriodChange(e.target.value)}
            disabled={loading}
            className={CHART_TOOLBAR_SELECT_CLASS}
          >
            {PERIODS.map(({ id, label }) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {loading ? (
        <Skeleton className="w-full rounded-lg" style={{ height: `${height}px` }} aria-hidden />
      ) : (
        <Chart
          type={chartType === 'bar' ? 'bar' : 'area'}
          height={height}
          series={[{ name: seriesName, data: seriesToRender }]}
          options={chartOptions}
        />
      )}
    </div>
  );
};

export default DeploymentFrequencyChart;
